import { useQuery, useLazyQuery } from '@apollo/client';
import {
  ALL_MODULES,
  AsyncPropSelector,
  CUSTOM_FIELDS,
  T1,
  getTranslatedText
} from '@nile/components';
import {
  GET_SECURE_FIELD_VALUE,
  AccountClient,
  SystrayAccountClient
} from '@nile/graphql';
import { DisabledTag } from '@nile/mangifera/src/Pebbles/TicketContainer';
import {
  Popover,
  TextField,
  Button,
  ButtonGroup
} from '@superops/ui-design-system';
import { Edit } from '@styled-icons/material/Edit';
import { cloneDeep, isEqual } from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';
import React, { useCallback, useState, useEffect } from 'react';
import styled from 'styled-components';
import { SVGIcon } from '@superops/super-icons';
import { EditIcon } from '@nile/components/src/Icons';
import { useCopyToClipboard } from '@nile/mangifera/src/Pages/MigrateDataPage/useCopyToClipboard';
import LoadingSuccess from '@nile/components/src/Svg/AnimatedSvg/LoadingSuccess.svg';
import TotpTimer from '@nile/rmm/src/modules/it-documentation/components/sub-pages/non-monitored-asset-info/TotpTimer';
import { GET_TOTP_FIELD_VALUE } from '@nile/rmm/src/modules/asset-management/Graphql/asset';

const CustomField = styled.div`
  min-width: 200px;
  max-width: ${(props) => (props?.fullWidth ? 'unset' : '250px')};
  flex-grow: 1;
  padding-right: ${(props) => !props?.fullWidth && '10px'};
  padding-left: ${(props) => !props?.fullWidth && '10px'};
  margin-top: 8px;
  margin-bottom: 8px;
  .Polaris-ButtonGroup {
    margin-top: 12px;
  }

  .secure-field-input-wrapper div:first-child {
    width: 100%;
  }
`;

const Label = styled.div`
  text-align: left;
  font-weight: 500 !important;
  color: rgba(0, 0, 0, 0.6);
  margin-top: 10px;
`;

const Wrap = styled.div`
  display: flex;
  align-items: center;
  .edit-actions {
    visibility: hidden;
    cursor: pointer;
  }
  padding-left: 8px;
  padding-right: 8px;
  max-width: 100%;
`;

const CField = styled.div`
  height: 100%;
  max-height: 40px;
  display: flex;
  align-items: center;
  .editable-cell,
  .cell {
    flex-grow: 1;
    font-weight: bold;
  }
  &:hover {
    .edit-actions {
      visibility: visible;
    }
  }
`;

function SecureText(props) {
  const {
    field,
    ticket,
    defaultValue,
    updateSelection,
    editable,
    value,
    inlineEdit,
    disableSelection,
    entityId,
    isMandatory,
    error,
    systray = false,
    timer = false,
    isTotp = false
  } = props;

  const intl = useIntl();

  const [inputType, setInputType] = useState(!value ? 'text' : 'password');
  const [updatedValue, setUpdatedValue] = useState(value || null);
  const [totpUpdatedValue, setTotpUpdatedValue] = useState(defaultValue);
  const [totpLoading, setTotpLoading] = useState(false);
  const [copied, copyText] = useCopyToClipboard({
    revertCopyStateTimeout: 1500
  });
  const [viewing, setViewing] = useState(false);
  const [
    getSecureFieldValue,
    {
      data: secureFieldValue,
      loading: secureFieldLoading,
      error: secureFieldError
    }
  ] = useLazyQuery(GET_SECURE_FIELD_VALUE, {
    client: systray ? SystrayAccountClient : AccountClient,
    fetchPolicy: 'network-only',
    onCompleted(res) {
      if (res?.getSecureFieldValue) {
        if (inlineEdit) {
          if (isTotp) {
            setTotpUpdatedValue(res?.getSecureFieldValue);
          } else {
            setUpdatedValue(res?.getSecureFieldValue);
          }
        } else {
          updateSelection(res?.getSecureFieldValue);
        }
        setInputType('text');
      }
    }
  });
  const [getTotpFieldValue] = useLazyQuery(GET_TOTP_FIELD_VALUE, {
    client: systray ? SystrayAccountClient : AccountClient,
    fetchPolicy: 'network-only',
    onCompleted(res) {
      if (res?.getTotpFieldValue) {
        setUpdatedValue(res?.getTotpFieldValue?.OTP);
      }
      setTotpLoading(false);
    },
    onError() {
      setTotpLoading(false);
    }
  });

  const [
    getSecureFieldViewValue,
    {
      data: secureFieldViewValue,
      loading: secureFieldViewLoading,
      error: secureFieldViewError
    }
  ] = useLazyQuery(GET_SECURE_FIELD_VALUE, {
    client: systray ? SystrayAccountClient : AccountClient,
    fetchPolicy: 'network-only',
    onCompleted(res) {
      if (res?.getSecureFieldValue) {
        if (inlineEdit) {
          setUpdatedValue(res?.getSecureFieldValue);
        } else {
          updateSelection(res?.getSecureFieldValue);
        }
        setViewing(true);
      }
    }
  });
  const [
    getTotpFieldViewValue,
    { data: totpFieldViewValue, refetch: refetchTotpFieldViewValue }
  ] = useLazyQuery(GET_TOTP_FIELD_VALUE, {
    client: systray ? SystrayAccountClient : AccountClient,
    fetchPolicy: 'no-cache',
    onCompleted(res) {
      if (res?.getTotpFieldValue) {
        if (inlineEdit) {
          setUpdatedValue(res?.getTotpFieldValue?.OTP);
        } else {
          updateSelection(res?.getTotpFieldValue?.OTP);
        }
        setViewing(true);
      }
      setTotpLoading(false);
    },
    onError() {
      setTotpLoading(false);
    }
  });

  const [
    getSecureCopiedValue,
    {
      data: securedCopyText,
      loading: securedCopyTextLoading,
      error: securedCopyTextError
    }
  ] = useLazyQuery(GET_SECURE_FIELD_VALUE, {
    client: systray ? SystrayAccountClient : AccountClient,
    fetchPolicy: 'network-only',
    onCompleted({ getSecureFieldValue }) {
      if (inputType === 'text') {
        setViewing(false);
      }
      copyText(getSecureFieldValue);
    }
  });
  const [getTotpCopiedValue, { data: totpCopiedText }] = useLazyQuery(
    GET_TOTP_FIELD_VALUE,
    {
      client: systray ? SystrayAccountClient : AccountClient,
      fetchPolicy: 'no-cache',
      onCompleted({ getTotpFieldValue: totpFieldValue }) {
        if (inputType === 'text') {
          setViewing(false);
        }
        copyText(totpFieldValue?.OTP);
        setTotpLoading(false);
      },
      onError() {
        setTotpLoading(false);
      }
    }
  );
  const refetchTotpFieldValueWithLoading = async (variables) => {
    setTotpLoading(true);
    return getTotpFieldValue({
      variables: {
        input: {
          module: props.module,
          entityId,
          customFieldName: field.columnName || field.name
        }
      }
    }).finally(() => {
      setTotpLoading(false);
    });
  };
  function CopyButton() {
    return copied ? (
      <div className="successIcon ml-5">
        <LoadingSuccess />
      </div>
    ) : (
      <div
        onClick={() => {
          if (viewing && !popoverActive) {
            copyText(updatedValue);
          } else {
            const variables = {
              variables: {
                input: {
                  module: props.module,
                  entityId,
                  customFieldName: field.columnName || field.name
                }
              }
            };
            if (isTotp) {
              setTotpLoading(true);
              getTotpCopiedValue(variables);
            } else {
              getSecureCopiedValue(variables);
            }
          }
        }}
        className="cursor-pointer ml-5 fx"
      >
        <SVGIcon name="CopyClipboard" color="rgb(140, 133, 135)" />
      </div>
    );
  }

  useEffect(() => {
    // if (!isEqual(updatedValue, value)) {
    //   setUpdatedValue(value);
    // }
  }, [value]);
  useEffect(() => {}, [inputType]);
  const updateSelectedField = (val) => {
    const type = field.fieldType || field.uiDataType;
    const parsedVal = val;
    updateSelection(parsedVal || null);
  };
  const [popoverActive, setPopoverActive] = useState(false);
  const togglePopoverActive = useCallback(() => {
    setPopoverActive(!popoverActive);
    setInputType(!value ? 'text' : 'password');
  });

  const updateUpdatedValue = (val) => {
    if (isTotp) {
      setTotpUpdatedValue(val || null);
    } else {
      setUpdatedValue(val || null);
    }
  };

  const getField = () => {
    const type = field.fieldType || field.uiDataType;
    const updatedFieldValue = isTotp ? totpUpdatedValue : updatedValue;

    switch (type) {
      case 'secure_text':
      case CUSTOM_FIELDS.secureText:
      case CUSTOM_FIELDS.totp:
        return (
          <div
            className={`secure-field-input-wrapper flex center ${
              secureFieldLoading || totpLoading ? 'disabled' : ''
            }`}
          >
            <TextField
              onChange={inlineEdit ? updateUpdatedValue : updateSelectedField}
              value={inlineEdit ? updatedFieldValue : value}
              placeholder={getTranslatedText(
                intl,
                'common.placeholder.enterValue'
              )}
              // disabled={disableSelection || inputType === 'password'}
              type={inputType}
              error={error}
            />
            <div
              className="ml8 pointer flex center"
              onClick={() => {
                if (inputType === 'password') {
                  if (
                    (value === updatedFieldValue && inlineEdit) || // for creation forms exclusion
                    (!inlineEdit && value === '*******') // for edit form inclusion
                  ) {
                    getSecureFieldValue({
                      variables: {
                        input: {
                          module: props.module,
                          entityId,
                          customFieldName: field.columnName || field.name
                        }
                      }
                    });
                  } else {
                    setInputType('text');
                  }
                } else {
                  setInputType('password');
                }
              }}
            >
              <SVGIcon
                name={inputType === 'password' ? 'EyeHidden' : 'EyeVisible'}
              />
            </div>
          </div>
        );
      default:
    }
  };
  const activator = <EditIcon onClick={() => togglePopoverActive()} />;
  const getTag = () => {
    return (
      <CustomField className="cf-wrapper">
        <Label className="cf-label">
          {field.label || ''}{' '}
          {isMandatory && (
            <>
              <span className="mandatory-star">*</span>
            </>
          )}
        </Label>
        {getField()}
        <div className="mt10">
          <ButtonGroup>
            <Button
              onClick={() => {
                togglePopoverActive();
                if (isTotp) {
                  setTotpUpdatedValue(value || '');
                } else {
                  setUpdatedValue(value || '');
                  setViewing(false);
                }
              }}
            >
              <FormattedMessage id="common.cancel" />
            </Button>
            <Button
              primary
              onClick={() => {
                updateSelectedField(isTotp ? totpUpdatedValue : updatedValue);
                togglePopoverActive();
                setViewing(false);
              }}
            >
              <FormattedMessage id="common.save" />
            </Button>
          </ButtonGroup>
        </div>
      </CustomField>
    );
  };
  const displayVal = value;
  const renderTotpTimer = () => {
    return (
      <>
        {timer &&
          viewing &&
          (totpFieldViewValue?.getTotpFieldValue?.expiryTimeInSeconds ||
            totpCopiedText?.getTotpFieldValue?.expiryTimeInSeconds) && (
            <TotpTimer
              expiryTime={
                totpFieldViewValue?.getTotpFieldValue?.expiryTimeInSeconds ||
                totpCopiedText?.getTotpFieldValue?.expiryTimeInSeconds
              }
              refetchTotpFieldValue={refetchTotpFieldValueWithLoading}
              module={props.module}
              entityId={entityId}
              customFieldName={field.columnName || field.name}
              viewing={viewing}
              setViewing={setViewing}
            />
          )}
      </>
    );
  };
  // if (loading) return <div>loading...</div>;
  if (inlineEdit) {
    if (viewing && !popoverActive) {
      return (
        <CField
          className={`${
            secureFieldViewLoading || securedCopyTextLoading || totpLoading
              ? 'disabled'
              : ''
          }`}
        >
          <Wrap className={`${editable ? 'editable-cell' : 'cell'}`}>
            <div className="ellipsis" title={displayVal}>
              {updatedValue}
            </div>
            <div
              className="ml8 pointer flex center"
              onClick={() => {
                setViewing(false);
              }}
            >
              <SVGIcon name="EyeVisible" />
            </div>
            <CopyButton />
            {renderTotpTimer()}
            {editable && <div className="edit-actions">{activator}</div>}
          </Wrap>
        </CField>
      );
      // eslint-disable-next-line no-else-return
    } else if (popoverActive) {
      return (
        <CField>
          <Wrap className="editable-cell">
            {viewing && (
              <div className="ellipsis" title={displayVal}>
                {updatedValue}
              </div>
            )}
            {!viewing && (
              <div className="ellipsis" title={displayVal}>
                {displayVal ? '*******' : <DisabledTag>NA</DisabledTag>}
              </div>
            )}
            <Popover
              activator={activator}
              active={popoverActive}
              onClose={() => {
                // togglePopoverActive()
              }}
            >
              {getTag()}
            </Popover>
            {renderTotpTimer()}
          </Wrap>
        </CField>
      );
      // eslint-disable-next-line no-else-return
    } else {
      return (
        <CField
          className={`${
            secureFieldViewLoading || securedCopyTextLoading || totpLoading
              ? 'disabled'
              : ''
          }`}
        >
          <Wrap className={`${editable ? 'editable-cell' : 'cell'}`}>
            <div className="ellipsis" title={displayVal}>
              {displayVal ? '*******' : <DisabledTag>NA</DisabledTag>}
            </div>
            {displayVal && (
              <div
                className="ml8 pointer flex center"
                onClick={() => {
                  const variables = {
                    variables: {
                      input: {
                        module: props.module,
                        entityId,
                        customFieldName: field.columnName || field.name
                      }
                    }
                  };
                  if (isTotp) {
                    setTotpLoading(true);
                    getTotpFieldViewValue(variables);
                  } else {
                    getSecureFieldViewValue(variables);
                  }
                }}
              >
                <SVGIcon name="EyeHidden" />
              </div>
            )}
            <CopyButton />

            {renderTotpTimer()}
            {editable && <div className="edit-actions mt-5">{activator}</div>}
          </Wrap>
        </CField>
      );
    }
  }
  return (
    <CustomField fullWidth={props?.fullWidth} className="cf-wrapper">
      <Label className="cf-label flex">
        <T1>{field.label || ''}</T1>
        {isMandatory && (
          <>
            <span className="mandatory-star">*</span>
          </>
        )}
      </Label>
      <div className="flex col" key={field.name}>
        {getField()}
      </div>
    </CustomField>
  );
}

export default SecureText;
